import { ref, computed, readonly } from 'vue';

// =============================================================================
// useTheme — pilote le thème clair/sombre.
// Pose data-theme sur <html>, persiste le choix, et suit prefers-color-scheme
// quand le mode est "system".
// =============================================================================

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'FYGR_THEME';

const media = window.matchMedia('(prefers-color-scheme: dark)');

function readStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : 'system';
}

// État partagé (singleton module-level)
const mode = ref<ThemeMode>(readStoredMode());

// Verrou « clair forcé » : certaines zones (sign-in, tunnel d'onboarding, sous-
// domaine auth) doivent toujours s'afficher en clair, indépendamment de la
// préférence de l'utilisateur — qui reste conservée pour l'intérieur de l'app.
const forcedLight = ref(false);

function resolve(m: ThemeMode): 'light' | 'dark' {
  if (m === 'system') return media.matches ? 'dark' : 'light';
  return m;
}

function apply() {
  const theme = forcedLight.value ? 'light' : resolve(mode.value);
  document.documentElement.setAttribute('data-theme', theme);
  // Notifie les vues impératives (amCharts…) qui doivent se redessiner sur
  // changement de thème — elles ne réagissent pas au CSS seul.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('fy-theme-change'));
  }
}

// Réagit aux changements système uniquement en mode "system"
media.addEventListener('change', () => {
  if (mode.value === 'system' && !forcedLight.value) apply();
});

/**
 * Verrouille (ou déverrouille) l'affichage en clair. Ne modifie pas la
 * préférence stockée : à la sortie de la zone verrouillée, le thème choisi par
 * l'utilisateur est réappliqué. À piloter depuis le guard de routeur.
 */
export function setForceLight(value: boolean) {
  if (forcedLight.value === value) return;
  forcedLight.value = value;
  apply();
}

export function setTheme(m: ThemeMode) {
  mode.value = m;
  localStorage.setItem(STORAGE_KEY, m);
  apply();
}

export function toggleTheme() {
  setTheme(resolve(mode.value) === 'dark' ? 'light' : 'dark');
}

/** À appeler une fois au boot (main.ts), avant le render. */
export function initTheme() {
  apply();
}

export function useTheme() {
  return {
    mode: readonly(mode),
    resolved: computed(() => resolve(mode.value)),
    isDark: computed(() => resolve(mode.value) === 'dark'),
    setTheme,
    toggleTheme,
  };
}
